// frontend/src/components/dashboard/ClientHistoryDialog.js
import React, { useState, useEffect } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   List,
   ListItem,
   ListItemText,
   Typography,
   Button,
   Divider,
   Box,
   TextField,
   IconButton,
   FormControl,
   InputLabel,
   Select,
   MenuItem
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axiosInstance from '../../utils/axios';

export default function ClientHistoryDialog({ open, onClose, client }) {
   const [history, setHistory] = useState([]);
   const [newNote, setNewNote] = useState({
       type: 'note',
       content: '',
       nextStep: ''
   });

   useEffect(() => {
       if (client) fetchHistory();
   }, [client]);

   const fetchHistory = async () => {
       try {
           const response = await axiosInstance.get(`/clients/${client._id}/history`);
           setHistory(response.data);
       } catch (error) {
           console.error('Error:', error);
       }
   };

   const addHistoryItem = async () => {
       if (!newNote.content.trim()) return;

       try {
           await axiosInstance.post(`/clients/${client._id}/history`, {
               type: newNote.type,
               note: newNote.content,
               nextStep: newNote.nextStep,
               timestamp: new Date().toISOString()
           });
           setNewNote({ type: 'note', content: '', nextStep: '' });
           fetchHistory();
       } catch (error) {
           console.error('Error:', error);
       }
   };

   const deleteHistoryItem = async (itemId) => {
       try {
           await axiosInstance.delete(`/clients/${client._id}/history/${itemId}`);
           fetchHistory();
       } catch (error) {
           console.error('Error:', error);
       }
   };

   return (
       <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
           <DialogTitle>
               History - {client?.name}
           </DialogTitle>
           <DialogContent>
               <Box sx={{ mb: 3 }}>
                   <FormControl fullWidth sx={{ mb: 2 }}>
                       <InputLabel>Interaction Type</InputLabel>
                       <Select
                           value={newNote.type}
                           label="Interaction Type"
                           onChange={(e) => setNewNote({...newNote, type: e.target.value})}
                       >
                           <MenuItem value="call">Call</MenuItem>
                           <MenuItem value="message">Message</MenuItem>
                           <MenuItem value="email">Email</MenuItem>
                           <MenuItem value="meeting">Meeting</MenuItem>
                           <MenuItem value="note">Note</MenuItem>
                       </Select>
                   </FormControl>
                   <TextField
                       fullWidth
                       multiline
                       rows={2}
                       label="Details"
                       value={newNote.content}
                       onChange={(e) => setNewNote({...newNote, content: e.target.value})}
                       sx={{ mb: 2 }}
                   />
                   <TextField
                       fullWidth
                       label="Next Steps"
                       value={newNote.nextStep}
                       onChange={(e) => setNewNote({...newNote, nextStep: e.target.value})}
                       sx={{ mb: 2 }}
                   />
                   <Button
                       variant="contained"
                       startIcon={<AddIcon />}
                       onClick={addHistoryItem}
                       disabled={!newNote.content.trim()}
                   >
                       Add Entry
                   </Button>
               </Box>
               <Divider />
               <List>
                   {history.map((item) => (
                       <ListItem
                           key={item._id}
                           divider
                           secondaryAction={
                               <IconButton 
                                   edge="end" 
                                   onClick={() => deleteHistoryItem(item._id)}
                                   size="small"
                               >
                                   <DeleteIcon />
                               </IconButton>
                           }
                       >
                           <ListItemText
                               primary={
                                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                       <Typography variant="subtitle1">
                                           {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                                       </Typography>
                                       <Typography variant="caption" color="textSecondary">
                                           {new Date(item.timestamp).toLocaleString()}
                                       </Typography>
                                   </Box>
                               }
                               secondary={
                                   <>
                                       <Typography component="div">
                                           {item.note}
                                       </Typography>
                                       {item.nextStep && (
                                           <Typography component="div" color="primary">
                                               Next Step: {item.nextStep}
                                           </Typography>
                                       )}
                                       <Typography variant="caption" color="textSecondary">
                                           By: {item.createdBy?.name || 'System'}
                                       </Typography>
                                   </>
                               }
                           />
                       </ListItem>
                   ))}
               </List>
           </DialogContent>
       </Dialog>
   );
}